import React, { useEffect, useState, MouseEvent } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { GET_PLANS } from "./queries";
import { PlanType, SubscriptionProductType } from "../../../models/plans";
import { LoaderBar } from "../../../middleware/loaders";
import Plans from "./plans";
import Payment from "./payment";
import { PageSettingItem } from "../../../models/pageSettings";
import { LoaderDots } from "../../../middleware/main_loader";
import PromoCode from "./promo_code";
import { PromoCodeType } from "../../../models/promocodes";
import { getAuthCookie } from "../../../middleware/cookie-manager";

type Views = "plans" | "payment";

const PurchasePlan: React.FC = () => {
  const user = getAuthCookie(true);
  const { data, loading } = useQuery<{
    plans: PlanType[];
    pageSettings: PageSettingItem[];
  }>(GET_PLANS, { fetchPolicy: "no-cache" });
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [products, setProducts] = useState<SubscriptionProductType[]>([]);
  const [promo_code, setPromoCode] = useState<PromoCodeType | undefined>();
  const [selectedPlan, setPlan] = useState<SubscriptionProductType | undefined>(
    undefined,
  );
  const [view, setView] = useState<Views>("plans");
  const [is_loyalty, setLoyalty] = useState<boolean>(false);

  useEffect(() => {
    const document_body = document.getElementsByTagName("body")[0];
    if (document_body) {
      document_body.classList.add("white_bg");
    }
    return () => {
      document_body.classList.remove("white_bg");
    };
  }, []);

  useEffect(() => {
    if (data?.plans) {
      ///search for loyalty_pricing inside the plans
      const loyalty = data.plans.find((p) => p.loyalty_price);
      if (loyalty) {
        setLoyalty(true);
      }
      const set_products = data.plans.map((product) => {
        let price = product.price_amount;
        if (promo_code) {
          if (promo_code.type === "discount_cash") {
            price = price - parseFloat(promo_code.value);
          }
        }
        if (loyalty && product.loyalty_price) {
          price = product.loyalty_price;
        }
        return {
          id: product.id,
          plan_id: product.id,
          name: `Flangoo Library™ \n${product.name}`,
          secondary_pricing: 0,
          secondary_pricing_regular: 0,
          price: price,
        } as SubscriptionProductType;
      });
      setProducts(set_products);
      setPlans(data.plans);
    }
    //eslint-disable-next-line
  }, [data]);

  const handlePlanSelect = (ev: MouseEvent<HTMLButtonElement>) => {
    const product_id = ev.currentTarget.getAttribute("data-plan-id");
    if (!product_id) return;
    const product = products.find((p) => p.id === product_id);
    console.log("index.tsx:111 | product", product);
    if (!product) return;
    setPlan(product);
    ///if selected addon product send to payment
    if (product_id === "4") {
      setView("payment");
    }
  };

  const handlePageView = (ev: MouseEvent<HTMLButtonElement>) => {
    const set_view = ev.currentTarget.getAttribute("data-view") as Views;
    if (!set_view) return;
    setView(set_view);
  };

  if (loading) {
    return (
      <main className="first-container bg-light py-5 text-center ">
        <LoaderBar fixed={true} />
        <LoaderDots size={20} />
        <p className="mt-3 text-muted">Getting Plans</p>
      </main>
    );
  }
  let title_txt = "Purchase Your Flangoo Membership";
  if (is_loyalty) {
    title_txt = "Renew Your Membership Today with Loyalty Pricing";
  }

  const onApplyPromoCode = (promo_Code: PromoCodeType) => {
    ///update plans according to promo code
    const new_plans = plans.map((plan) => {
      if (promo_Code.type === "discount_cash" && plan.id !== "1") {
        let loyalty_price = plan.loyalty_price ? plan.loyalty_price : 0;
        let regular_price =
          plan.regular_price > 1 ? plan.regular_price : plan.price_amount;
        let new_price = plan.price_amount - parseFloat(promo_Code.value);
        ///set price to two decimals
        new_price = Math.round(new_price * 100) / 100;
        if (plan.loyalty_price) {
          loyalty_price = plan.loyalty_price - parseFloat(promo_Code.value);
          loyalty_price = Math.round(loyalty_price * 100) / 100;
        }
        return {
          ...plan,
          price_amount: new_price,
          regular_price,
          loyalty_price,
          has_promo: true,
        };
      }
      return plan;
    });
    const new_products = products.map((product: SubscriptionProductType) => {
      const plan: PlanType | undefined = new_plans.find(
        (p) => p.id === product.plan_id,
      );
      if (promo_Code.type === "discount_cash" && product.id !== "1") {
        if (plan) {
          const product_plan = new_plans.find((p) => p.id === product.plan_id);
          if (product_plan) {
            return {
              ...product,
              plan: product_plan,
              price: product_plan.price_amount + product.secondary_pricing,
            };
          }
        }
      }
      if (plan) {
        return { ...product, plan, price: plan.price_amount };
      }
      return product;
    });
    setProducts(new_products);
    setPromoCode(promo_Code);

    setPlans(new_plans);
  };

  const removeAppliedCode = () => {
    if (!data || !data.plans) {
      return;
    }
    setPromoCode(undefined);
    setPlans(data.plans);
  };

  const showSpecialPrice = (date: string): boolean => {
    const targetDate = new Date(date);
    const endDate = new Date("2025-09-01");
    return targetDate <= endDate;
  };

  return (
    <main className="first-container bg-light py-5">
      <div className="container">
        <h3 className="black_text">{title_txt}</h3>
        <hr />
        <div className="row justify-content-between">
          {view === "plans" && plans ? (
            <Plans
              plans={plans}
              products={products}
              selectedPlan={selectedPlan}
              handlePlanSelect={handlePlanSelect}
              show_special_price={
                user.plan ? showSpecialPrice(user.plan.to) : false
              }
              is_loyalty={is_loyalty}
            />
          ) : null}
          {view === "payment" && selectedPlan ? (
            <Payment
              selectedPlan={selectedPlan}
              paypal_setting={data?.pageSettings[0]}
              handlePageView={handlePageView}
              promo_code={promo_code}
            />
          ) : null}
          <div className="col-md-10 d-flex justify-content-between align-items-center">
            <Link
              to="/youraccount"
              className="btn btn-dark text-white green_contain_gray"
            >
              <i className="fas fa-angle-left" aria-hidden="true"></i> Go Back
            </Link>
            {view === "plans" ? (
              <PromoCode
                onApplyPromoCode={onApplyPromoCode}
                isHorizontal={true}
                removeAppliedCode={removeAppliedCode}
              />
            ) : null}
            <div className="d-flex flex-column align-items-center position-relative">
              {view === "plans" ? (
                <button
                  onClick={handlePageView}
                  data-view="payment"
                  className="btn btn-outline-danger  btn_lg green_contain_green"
                  disabled={selectedPlan ? false : true}
                >
                  Pay Now
                </button>
              ) : null}
              {view === "plans" ? (
                <div
                  className={`text-center ${!selectedPlan ? "" : "aria-hidden opacity_0"}`}
                  aria-hidden={!selectedPlan ? "false" : "true"}
                >
                  <small
                    className="mb-0 text-muted"
                    style={{
                      position: "absolute",
                      top: "102%",
                      width: " 100%",
                      right: "100%",
                      whiteSpace: "nowrap",
                      transform: "translateX(30%)",
                    }}
                  >
                    Please select a plan to continue to payment.
                  </small>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PurchasePlan;
